/**
 * Character smoke — Casa Aura (Variant C)
 *
 * Inspired by the Pretext fluid-smoke demo:
 * characters/symbols behave like smoke particles.
 * Smoke rendered as rising glyphs — ✦ ∿ ~ ○ ° · ✧ ❋
 * Creates a branded, abstract, typographic feel.
 */
(function (root) {
  'use strict';
  if (root.SmokeChars) return;

  var CHARS = ['✦', '∿', '~', '·', '○', '°', '✧', '❋', '◦', '∾', '⁓'];

  function CharParticle(ox, oy) {
    this.reset(ox, oy);
  }

  CharParticle.prototype.reset = function (ox, oy) {
    this.x    = ox + (Math.random() - 0.5) * 8;
    this.y    = oy;
    this.vx   = (Math.random() - 0.5) * 0.35;
    this.vy   = -(Math.random() * 0.60 + 0.50);
    this.char = CHARS[Math.floor(Math.random() * CHARS.length)];
    this.baseSize = Math.random() * 10 + 10; // font size at origin
    this.size = this.baseSize;
    this.life    = 0;
    this.maxLife = Math.floor(Math.random() * 240 + 320); // longer — travel to top
    this.rotation = Math.random() * Math.PI * 2;
    this.rotSpeed = (Math.random() - 0.5) * 0.018;
    this.turbOffset = Math.random() * 1000;
    this.alpha = 0;
    this.maxAlpha = 0.50 + Math.random() * 0.30;
  };

  CharParticle.prototype.update = function (t, ox, oy, canvasH) {
    this.life++;
    if (this.life >= this.maxLife) { this.reset(ox, oy); return; }

    var progress = this.life / this.maxLife;

    // Rise ratio (0 at origin, 1 at canvas top)
    var rise = Math.max(0, (oy - this.y) / (canvasH || oy));

    // Gentle turbulence increases with height
    var turb = 0.25 + rise * 0.8;
    this.vx += Math.sin(t * 0.022 + this.turbOffset) * 0.028 * turb;
    this.vx *= 0.965;
    this.x  += this.vx;
    this.y  += this.vy;
    this.vy *= 0.998;

    this.rotation += this.rotSpeed;

    // Characters shrink as they travel away — distant smoke appears smaller
    this.size = this.baseSize * Math.max(0.25, 1 - rise * 0.80);

    // Opacity
    if (progress < 0.08) {
      this.alpha = this.maxAlpha * (progress / 0.08);
    } else if (progress < 0.75) {
      this.alpha = this.maxAlpha;
    } else {
      this.alpha = this.maxAlpha * (1 - (progress - 0.70) / 0.30);
    }
  };

  CharParticle.prototype.draw = function (ctx) {
    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.rotate(this.rotation);
    ctx.globalAlpha = this.alpha;
    ctx.fillStyle = 'rgb(85,88,95)';
    ctx.font = this.size + 'px serif';
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.char, 0, 0);
    ctx.restore();
  };

  function SmokeChars(canvas, opts) {
    this.canvas = canvas;
    this.ctx    = canvas.getContext('2d');
    this.opts   = Object.assign({ autoplay: true, showEmber: true }, opts || {});

    this.particles = [];
    this.time      = 0;
    this.running   = false;
    this._raf      = null;
    this._frame    = 0;

    this._resize = this._resize.bind(this);
    this._tick   = this._tick.bind(this);
    window.addEventListener('resize', this._resize);
    this._doResize();

    // Pre-spawn staggered
    for (var i = 0; i < 16; i++) {
      var p = new CharParticle(this.ox, this.oy);
      p.life = Math.floor(Math.random() * p.maxLife);
      this.particles.push(p);
    }

    if (this.opts.autoplay) this._setupIO();
  }

  SmokeChars.prototype._doResize = function () {
    var parent = this.canvas.parentElement;
    this.canvas.width  = parent.offsetWidth;
    this.canvas.height = parseInt(this.canvas.dataset.height, 10) || 400;
    this.ox = this.canvas.width / 2;
    // data-origin-bottom lets the section pin origin to the CTA button center
    var ob = parseInt(this.canvas.dataset.originBottom, 10);
    this.oy = ob > 0 ? this.canvas.height - ob : this.canvas.height * 0.92;
    // Clear stale particles — they were positioned for the old canvas size
    if (this.particles) this.particles.length = 0;
  };

  SmokeChars.prototype._resize = function () {
    var self = this;
    clearTimeout(this._resizeDebounce);
    this._resizeDebounce = setTimeout(function () { self._doResize(); }, 120);
  };

  SmokeChars.prototype._setupIO = function () {
    var self = this;
    new IntersectionObserver(function (e) {
      e.forEach(function (en) { en.isIntersecting ? self.start() : self.stop(); });
    }, { threshold: 0.05 }).observe(this.canvas);
  };

  SmokeChars.prototype.start = function () {
    if (this.running) return;
    this.running = true;
    this._raf = requestAnimationFrame(this._tick);
  };

  SmokeChars.prototype.stop = function () {
    this.running = false;
    if (this._raf) { cancelAnimationFrame(this._raf); this._raf = null; }
  };

  SmokeChars.prototype._tick = function () {
    if (!this.running) return;
    this.time++;
    this._frame++;

    var ctx = this.ctx;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    if (this._frame % 3 === 0 && this.particles.length < 42) {
      this.particles.push(new CharParticle(this.ox, this.oy));
    }

    // Ember
    if (this.opts.showEmber) {
      var g = ctx.createRadialGradient(this.ox, this.oy, 0, this.ox, this.oy, 10);
      g.addColorStop(0, 'rgba(255,200,90,0.14)');
      g.addColorStop(1, 'rgba(130,35,0,0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(this.ox, this.oy, 10, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(255,230,160,0.45)';
      ctx.beginPath();
      ctx.arc(this.ox, this.oy, 1, 0, Math.PI * 2);
      ctx.fill();
    }

    var cH = this.canvas.height;
    for (var i = 0; i < this.particles.length; i++) {
      this.particles[i].update(this.time, this.ox, this.oy, cH);
      this.particles[i].draw(ctx);
    }

    this._raf = requestAnimationFrame(this._tick);
  };

  root.SmokeChars = SmokeChars;
}(window));
